import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PubSubCommand, UserTypingEvent } from '../constants/pubsubCommands';

/** Publish an isTyping:true heartbeat at most this often while composing. */
export const TYPING_HEARTBEAT_MS = 2500;
/** Receivers drop a typist not heard from within this window (~2.4x heartbeat). */
export const TYPING_TTL_MS = 6000;
/** How often receivers sweep for expired typists. */
export const TYPING_SWEEP_MS = 1000;

export interface TypingUser {
  id: string;
  displayName: string;
  avatarUrl?: string;
  lastSeen: number;
}

interface UseTypingPresenceOptions {
  socket?: WebSocket;
  tenantId: string;
  groupId?: string;
  summaryId?: string;
  uId: string;
  selfDisplayName: string;
  selfAvatarUrl?: string;
}

interface UseTypingPresenceResult {
  typingUsers: TypingUser[];
  notifyDraftActivity: (hasContent: boolean) => void;
}

/**
 * Emits and receives ephemeral `user_typing` presence events over the existing
 * Azure Web PubSub socket. No persistence, no backend: clients publish directly
 * to the conversation group (the negotiate token grants webpubsub.sendToGroup).
 *
 * Stop detection is two-fold: an explicit isTyping:false when the draft empties
 * (clear or send), plus receiver-side TTL expiry when heartbeats cease (pause,
 * tab close, network drop) — so a stale indicator can never persist.
 *
 * Debug: `localStorage.setItem('debugShowOwnTyping', '1')` (read once per mount)
 * disables both self-suppression layers so your own typing round-trips through
 * Azure and renders locally. Remove the key to restore production behavior.
 */
export function useTypingPresence({
  socket,
  tenantId,
  groupId,
  summaryId,
  uId,
  selfDisplayName,
  selfAvatarUrl,
}: UseTypingPresenceOptions): UseTypingPresenceResult {
  const [typingMap, setTypingMap] = useState<Record<string, TypingUser>>({});
  const sentTypingRef = useRef(false);
  const lastSentAtRef = useRef(0);
  const debugSelfRef = useRef(
    typeof localStorage !== 'undefined' && localStorage.getItem('debugShowOwnTyping') === '1',
  );

  const sendFrame = useCallback(
    (isTyping: boolean) => {
      if (!socket || socket.readyState !== WebSocket.OPEN || !groupId || !tenantId || !uId) return;
      const payload: UserTypingEvent = {
        type: PubSubCommand.UserTyping,
        collaborationId: groupId,
        isTyping,
        senderId: uId,
        senderDisplayName: selfDisplayName,
        senderAvatarUrl: selfAvatarUrl ?? '',
        timestamp: new Date().toISOString(),
      };
      socket.send(
        JSON.stringify({
          type: 'sendToGroup',
          group: `${tenantId}_${groupId}`,
          dataType: 'json',
          noEcho: !debugSelfRef.current,
          data: JSON.stringify(payload),
        }),
      );
    },
    [socket, tenantId, groupId, uId, selfDisplayName, selfAvatarUrl],
  );

  const notifyDraftActivity = useCallback(
    (hasContent: boolean) => {
      const now = Date.now();
      if (hasContent) {
        if (!sentTypingRef.current || now - lastSentAtRef.current >= TYPING_HEARTBEAT_MS) {
          sendFrame(true);
          sentTypingRef.current = true;
          lastSentAtRef.current = now;
        }
      } else if (sentTypingRef.current) {
        sendFrame(false);
        sentTypingRef.current = false;
        lastSentAtRef.current = 0;
      }
    },
    [sendFrame],
  );


  useEffect(() => {
    const stop = () => {
      if (sentTypingRef.current) {
        sendFrame(false);
        sentTypingRef.current = false;
        lastSentAtRef.current = 0;
      }
    };
    window.addEventListener('pagehide', stop);
    return () => {
      window.removeEventListener('pagehide', stop);
      stop();
    };
  }, [sendFrame]);

  useEffect(() => {
    if (!socket || !groupId) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type !== 'message' || msg.from !== 'group' || msg.dataType !== 'json') return;
        const inner = JSON.parse(msg.data);
        const matches =
          inner.collaborationId === groupId || (!!summaryId && inner.collaborationId === summaryId);

        if (inner.type === PubSubCommand.UserTyping) {
          if (!matches || !inner.senderId) return;
          // Covers same-user-other-tab (noEcho only suppresses this connection).
          if (!debugSelfRef.current && inner.senderId.toLowerCase() === uId.toLowerCase()) return;
          const key = inner.senderId.toLowerCase();
          setTypingMap((prev) => {
            if (inner.isTyping) {
              return {
                ...prev,
                [key]: {
                  id: inner.senderId,
                  displayName: inner.senderDisplayName || 'Someone',
                  avatarUrl: inner.senderAvatarUrl,
                  lastSeen: Date.now(),
                },
              };
            }
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          });
          return;
        }

        // A participant's actual message clears their typing entry immediately.
        if (matches && inner.sender?.role === 'human' && inner.sender.id) {
          const key = inner.sender.id.toLowerCase();
          setTypingMap((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          });
        }
      } catch {
        // Malformed frame — ignore;
      }
    };

    socket.addEventListener('message', handleMessage);
    return () => {
      socket.removeEventListener('message', handleMessage);

      setTypingMap({});
    };
  }, [socket, groupId, summaryId, uId]);


  const hasTypers = Object.keys(typingMap).length > 0;
  useEffect(() => {
    if (!hasTypers) return;
    const timer = setInterval(() => {
      const cutoff = Date.now() - TYPING_TTL_MS;
      setTypingMap((prev) => {
        const live = Object.entries(prev).filter(([, u]) => u.lastSeen >= cutoff);
        return live.length === Object.keys(prev).length ? prev : Object.fromEntries(live);
      });
    }, TYPING_SWEEP_MS);
    return () => clearInterval(timer);
  }, [hasTypers]);

  const typingUsers = useMemo(
    () => Object.values(typingMap).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [typingMap],
  );

  return { typingUsers, notifyDraftActivity };
}
